/**
 * Viewer-side rules for the Roost live seat-window view (WI 36220), shared by Roost web and
 * Roost mobile (WI 36223). Pure and import-free so they are
 * tested without SignalR. The wire contract (POST /api/live-window and the
 * `liveWindowEvent` SignalR target) is documented in
 * documentation/solution-briefs/roost-live-window-stream.md.
 */

/** Matches the bridge's LiveWindowPolicy.ExpirySeconds. */
export const EXPIRY_MS = 45_000;

export type LiveWindowEventType =
  'liveWindowStarted' | 'liveWindowFrame' | 'liveWindowEnded';

export interface LiveWindowSession {
  seat: string;
  sessionId: string;
  windowTitle?: string | null;
  ownerName?: string | null;
  startedAt?: string;
  lastSeenAt?: string;
}

export interface LiveWindowEvent {
  type: LiveWindowEventType | string;
  seat: string;
  sessionId: string;
  seq?: number;
  width?: number;
  height?: number;
  jpegBase64?: string;
  receivedAt?: string;
  session?: LiveWindowSession | null;
  reason?: string;
}

export interface ShownFrame {
  seq: number;
  width: number;
  height: number;
  src: string;
  receivedAtMs: number;
}

export interface ViewerState {
  seat: string;
  sessionId: string | null;
  frame: ShownFrame | null;
  /** Null while live; otherwise why it ended: stopped, expired or disabled. */
  ended: string | null;
}

export function emptyViewer(seat: string): ViewerState {
  return { seat, sessionId: null, frame: null, ended: null };
}

export function frameSrc(jpegBase64: string): string {
  return `data:image/jpeg;base64,${jpegBase64}`;
}

/** Reads camelCase or PascalCase; null when the payload has no type, seat or session. */
export function normalizeLiveWindowEvent(raw: unknown): LiveWindowEvent | null {
  const r = (raw ?? {}) as Record<string, unknown>;
  const pick = (camel: string): unknown =>
    r[camel] ?? r[camel.charAt(0).toUpperCase() + camel.slice(1)];
  const type = pick('type');
  const seat = pick('seat');
  const sessionId = pick('sessionId');
  if (
    typeof type !== 'string' ||
    typeof seat !== 'string' ||
    typeof sessionId !== 'string' ||
    !seat ||
    !sessionId
  ) {
    return null;
  }
  return {
    type,
    seat,
    sessionId,
    seq: pick('seq') as number | undefined,
    width: pick('width') as number | undefined,
    height: pick('height') as number | undefined,
    jpegBase64: pick('jpegBase64') as string | undefined,
    receivedAt: pick('receivedAt') as string | undefined,
    session: pick('session') as LiveWindowSession | null | undefined,
    reason: pick('reason') as string | undefined,
  };
}

/**
 * Applies one event to the viewer. Events for another seat are ignored (one browser
 * connection can still sit in a previous seat's group). Within a session, a frame
 * whose seq is not newer than the shown one is dropped; a frame from a new session
 * (the seat restarted publishing) is followed.
 */
export function reduceViewer(
  state: ViewerState,
  event: LiveWindowEvent,
  nowMs: number
): ViewerState {
  if (event.seat.toLowerCase() !== state.seat.toLowerCase()) return state;

  switch (event.type) {
    case 'liveWindowStarted':
      return {
        seat: state.seat,
        sessionId: event.sessionId,
        frame: null,
        ended: null,
      };

    case 'liveWindowFrame': {
      if (!event.jpegBase64 || typeof event.seq !== 'number') return state;
      const sameSession = event.sessionId === state.sessionId;
      if (sameSession && state.frame && event.seq <= state.frame.seq)
        return state;
      return {
        seat: state.seat,
        sessionId: event.sessionId,
        ended: null,
        frame: {
          seq: event.seq,
          width: event.width ?? 0,
          height: event.height ?? 0,
          src: frameSrc(event.jpegBase64),
          receivedAtMs: nowMs,
        },
      };
    }

    case 'liveWindowEnded':
      if (state.sessionId !== null && event.sessionId !== state.sessionId)
        return state;
      return { ...state, ended: event.reason ?? 'stopped' };

    default:
      return state;
  }
}

/** Client-side backstop for AC 5: no frame for 45 s means the publisher is gone. */
export function withExpiry(state: ViewerState, nowMs: number): ViewerState {
  if (state.ended || !state.frame) return state;
  return nowMs - state.frame.receivedAtMs >= EXPIRY_MS
    ? { ...state, ended: 'expired' }
    : state;
}

/** Plain-language status line for the page. */
export function endedMessage(reason: string | null): string | null {
  switch (reason) {
    case null:
      return null;
    case 'expired':
      return 'The seat stopped sending. This window is no longer live.';
    case 'disabled':
      return 'Live window is turned off on the bridge.';
    default:
      return 'The seat stopped sharing this window.';
  }
}

/**
 * What a viewer shows for a /live-window reply (WI 36223, Roost mobile). The kill switch
 * answers every call with 404 {"error":"disabled"}; a bridge without the function at all
 * also answers 404, and both read "not enabled". A viewer off the terminal allowlist gets
 * 403, an expired or missing JWT 401: both read "no access", never an error dump.
 */
export type LiveWindowAccess =
  'ok' | 'disabled' | 'no-access' | 'not-live' | 'error';

export function liveWindowAccess(
  status: number,
  body?: unknown
): LiveWindowAccess {
  if (status >= 200 && status < 300) return 'ok';
  if (status === 401 || status === 403) return 'no-access';
  if (status === 404) {
    const error = (body as { error?: unknown } | null | undefined)?.error;
    return error === 'not-live' ? 'not-live' : 'disabled';
  }
  return 'error';
}

/** Plain-language line for a non-ok access state; null when ok. */
export function accessMessage(access: LiveWindowAccess): string | null {
  switch (access) {
    case 'ok':
      return null;
    case 'disabled':
      return 'Live window is not turned on for this bridge yet.';
    case 'no-access':
      return 'Your account is not on the live window viewer list. Ask Tim if you need access.';
    case 'not-live':
      return 'That seat is no longer sharing a window.';
    default:
      return 'Could not reach the bridge. Pull down to try again.';
  }
}
